import type { FetchError } from 'ofetch';
import type {
  GetXLSX,
  AddItemsToCart,
  UpdateItem,
  UpdateItems,
  PostXLS,
  DeleteItem,
  EmptyCart,
  DeleteItems,
  Checkout,
  UpdateRequestedDeliveryDate,
  UpdateAllRequestedDeliveryDates,
  ActivateInOrder,
  TransferCart,
  UpdateFavoriteItem,
  DeleteFavoriteItems,
  AddFavoriteItem,
  AddFavoriteItems,
  DeleteCart,
  GetXLSXResponse,
  AddItemsToCartResponse,
  UpdateItemResponse,
  UpdateItemsResponse,
  PostXLSResponse,
  FavoriteItem,
} from './types';
import type { HttpClient, HttpResponse } from '@/utils/HttpClient';
import type { CartItem } from '@/stores/cart';
import { FavoriteItemsPaged, FavoriteList } from '~/types/list';
import { CheckoutOrder } from '~/types/order';
import { Cart } from '~/types/cart';

export const CartService = (httpClient: HttpClient) => {
  return {
    getXLSX({ cartId, itemIds }: GetXLSX): GetXLSXResponse {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/export`,
        method: 'POST',
        body: itemIds,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    addItemsToCart({ cartId, items }: AddItemsToCart): AddItemsToCartResponse {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants/batch/add`,
        method: 'POST',
        body: items,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    updateItem({ cartId, itemId, ...body }: UpdateItem): UpdateItemResponse {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants/${itemId}`,
        method: 'PUT',
        body,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    updateItems({ cartId, items }: UpdateItems): UpdateItemsResponse {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants/batch/update`,
        method: 'PUT',
        body: items,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    postXLS({ cartId, formData: data }: PostXLS): PostXLSResponse {
      const headers: HeadersInit = new Headers();
      headers.append('Content-Type', 'multipart/form-data');
      return httpClient({
        endpoint: `/v2/cart/${cartId}/upload`,
        method: 'POST',
        body: data,
        headers,
        requestOptions: { omitErrorMessage: true },
      });
    },
    deleteItem({ cartId, itemId }: DeleteItem) {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants/${itemId}`,
        method: 'DELETE',
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    emptyCart({ cartId }: EmptyCart) {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants`,
        method: 'DELETE',
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    deleteItems({ cartId, itemIds }: DeleteItems) {
      return httpClient({
        endpoint: `/v2/cart/${cartId}/variants/batch/delete`,
        method: 'PUT',
        body: itemIds,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    checkout({ cartId, orderDetails }: Checkout): Promise<HttpResponse<CheckoutOrder[], FetchError>> {
      return httpClient<CheckoutOrder[], FetchError>({
        endpoint: `/v2/cart/${cartId}/checkout`,
        method: 'POST',
        body: orderDetails,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    updateRequestedDeliveryDate({ cartId, deliveryId, date: requestedDeliveryDate }: UpdateRequestedDeliveryDate) {
      return httpClient<CartItem[], string>({
        endpoint: `/v2/cart/${cartId}/delivery/${deliveryId}`,
        method: 'PUT',
        body: { requestedDeliveryDate },
        requestOptions: { omitErrorMessage: true },
      });
    },
    updateAllRequestedDeliveryDates({ cartId, date: requestedDeliveryDate }: UpdateAllRequestedDeliveryDates) {
      return httpClient<CartItem[], string>({
        endpoint: `/v2/cart/${cartId}/delivery`,
        method: 'PUT',
        body: { requestedDeliveryDate },
        requestOptions: { omitErrorMessage: true },
      });
    },
    activateInOrder({ cartId, deliveryId, isActive }: ActivateInOrder): Promise<HttpResponse<CartItem, FetchError>> {
      return httpClient<CartItem, FetchError>({
        endpoint: `/v2/cart/${cartId}/delivery/${deliveryId}/includeInOrder`,
        method: 'PUT',
        body: { isIncludedInOrder: isActive },
      });
    },
    transferCart({ sourceId, destId }: TransferCart) {
      return httpClient({
        endpoint: `/v2/cart/${destId}/transfer/${sourceId}`,
        method: 'PUT',
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
    getFavorites() {
      return httpClient<FavoriteList[]>({ endpoint: '/v2/favorites' });
    },
    getFavoriteItems(
      favoriteId: number,
      offset: number,
      limit: number,
    ): Promise<HttpResponse<FavoriteItemsPaged, FetchError>> {
      return httpClient<FavoriteItemsPaged, FetchError>({
        endpoint: `/v3/favorites/${favoriteId}/items?offset=${offset}&limit=${limit}`,
      });
    },
    getFavorite(favoriteId: number): Promise<HttpResponse<FavoriteList, FetchError>> {
      return httpClient<FavoriteList, FetchError>({ endpoint: `/v2/favorites/${favoriteId}` });
    },
    updateFavorite(favoriteId: number, favoriteName: string) {
      return httpClient({ endpoint: `/v2/favorites/${favoriteId}`, method: 'PUT', body: { name: favoriteName } });
    },
    updateFavoriteAccess(favoriteId: number, listAccess: string) {
      return httpClient({ endpoint: `/v2/favorites/${favoriteId}`, method: 'PUT', body: { access: listAccess } });
    },
    deleteFavorite(favoriteId: number) {
      return httpClient({ endpoint: `/v2/favorites/${favoriteId}`, method: 'DELETE' });
    },
    deleteFavoriteItems({
      favoriteId,
      itemIds,
    }: DeleteFavoriteItems): Promise<HttpResponse<FavoriteItem[], FetchError>> {
      return httpClient<FavoriteItem[], FetchError>({
        endpoint: `/v2/favorites/${favoriteId}/articles/batch/delete`,
        method: 'PUT',
        body: itemIds,
      });
    },
    updateFavoriteItem({ favoriteId, item }: UpdateFavoriteItem): Promise<HttpResponse<FavoriteItem, FetchError>> {
      const { favoriteItemId, quantity } = item;
      return httpClient<FavoriteItem, FetchError>({
        endpoint: `/v2/favorites/${favoriteId}/articles/${favoriteItemId}`,
        method: 'PUT',
        body: { quantity },
      });
    },
    addFavoriteItem({ favoriteId, item }: AddFavoriteItem) {
      const { articleNumber, quantity } = item;
      return httpClient({
        endpoint: `/v2/favorites/${favoriteId}/articles`,
        method: 'PUT',
        body: { articleNumber, quantity },
      });
    },
    addFavoriteItems({ favoriteId, items }: AddFavoriteItems) {
      return httpClient({
        endpoint: `/v2/favorites/${favoriteId}/articles`,
        method: 'PUT',
        body: items,
      });
    },
    deleteCart({ cartId }: DeleteCart): Promise<HttpResponse<Cart, FetchError>> {
      const body = { status: 'DELETED' };
      return httpClient<Cart, FetchError>({
        endpoint: `/v2/cart/${cartId}/status`,
        method: 'PUT',
        body,
        requestOptions: {
          omitErrorMessage: true,
        },
      });
    },
  };
};
